"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import { Home, Search, MessageCircle, Shield, ArrowRight, Zap, Sun } from "lucide-react";
import ReviewSection from "@/components/landing/ReviewSection";
import TrustBadgesSection from "@/components/landing/TrustBadgesSection";

const benefits = [
  { icon: Search, title: "Find Verified Providers", desc: "Browse certified manufacturers and distributors, filter by location, product type, ratings, and verified reviews." },
  { icon: MessageCircle, title: "Compare Multiple Quotes", desc: "Request quotes from several providers and compare pricing, warranties, and services, all in one place." },
  { icon: Shield, title: "Buy with Confidence", desc: "Verified reviews, provider certifications, and transparent pricing so every decision you make is fully informed." },
  { icon: Home, title: "End-to-End Support", desc: "From consultation to installation to recycling, we connect you with the right experts at every stage." },
];

const steps = [
  { n: "01", title: "Browse Products", desc: "Explore solar panels, inverters, batteries, and complete kits across hundreds of verified providers." },
  { n: "02", title: "Request Quotes", desc: "Share your energy needs, location, and budget. Get tailored quotes delivered directly to you." },
  { n: "03", title: "Compare & Choose", desc: "Review quotes side-by-side, check ratings, and pick the provider that fits your needs best." },
  { n: "04", title: "Go Solar!", desc: "Complete your purchase, get installed, and start enjoying clean, reliable power. It's that simple." },
];

const categories = [
  { label: "Solar Panels", desc: "High-efficiency panels for homes and businesses", href: "/marketplace?category=solar+panels" },
  { label: "Inverters", desc: "Convert solar power for all your appliances", href: "/marketplace?category=inverters" },
  { label: "Batteries", desc: "Store energy and use it any time, day or night", href: "/marketplace?category=batteries" },
  { label: "Solar Kits", desc: "Everything you need in one complete package", href: "/marketplace?category=solar+kits" },
  { label: "Charge Controllers", desc: "Optimise and protect your solar system", href: "/marketplace?category=charge+controllers" },
  { label: "Recycling Services", desc: "Responsibly dispose of old solar components", href: "/for-recyclers" },
];

export default function ForConsumersPage() {
  return (
    <div>
      {/* ── HERO ─────────────────────────────────────────── */}
      <section className="bg-[#002301] pt-28 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-10">
          <svg width="100%" height="100%"><defs><pattern id="con-dots" width="30" height="30" patternUnits="userSpaceOnUse"><circle cx="1.5" cy="1.5" r="1.5" fill="#17BC2E" /></pattern></defs><rect width="100%" height="100%" fill="url(#con-dots)" /></svg>
        </div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#FFB600]/8 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-[#17BC2E]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-6">
              <Home className="w-3.5 h-3.5 text-[#FFB600]" />
              <span className="text-white/80 text-xs font-medium">For Homeowners</span>
            </span>
            <h1 className="text-white font-bold text-4xl sm:text-5xl lg:text-6xl leading-tight mb-5">
              Power Your Home with<br /><span className="text-[#FFB600]">Clean Solar Energy</span>
            </h1>
            <p className="text-white/65 text-lg leading-relaxed max-w-2xl mx-auto mb-10">
              Nigeria's premier solar marketplace connects you with hundreds of certified providers.
              Compare quotes, verify credentials, and go solar, all in one place.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link href="/marketplace" className="inline-flex items-center justify-center gap-2 bg-[#FFB600] text-[#002301] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-[#17BC2E] hover:text-white transition-all duration-300 shadow-lg">
                Browse Marketplace <ArrowRight className="w-4 h-4" />
              </Link>
              <Link href="/solar-calculator" className="inline-flex items-center justify-center gap-2 bg-white/10 border border-white/25 text-white px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-white/20 transition-all duration-300 backdrop-blur-sm">
                <Sun className="w-4 h-4" /> Solar Calculator
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── STATS ────────────────────────────────────────── */}
      <section className="bg-white border-b border-gray-100 py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          {[
            { value: "8,000+", label: "Happy Customers" },
            { value: "340+", label: "Verified Providers" },
            { value: "80%", label: "Avg. Bill Reduction" },
            { value: "24/7", label: "Clean Power" },
          ].map(({ value, label }) => (
            <motion.div key={label} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <p className="text-[#002301] font-bold text-3xl">{value}</p>
              <p className="text-gray-500 text-sm mt-1">{label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── BENEFITS ─────────────────────────────────────── */}
      <section className="bg-[#f4f6f6] py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <motion.p initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-sm font-medium text-[#17BC2E] uppercase tracking-widest mb-3">Why Ren Power Group</motion.p>
            <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="text-[#002301] text-3xl sm:text-4xl font-bold">Built for Nigerian Homeowners</motion.h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {benefits.map(({ icon: Icon, title, desc }, i) => (
              <motion.div key={title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="bg-white rounded-2xl p-7 border border-gray-100 shadow-sm hover:shadow-md hover:border-[#17BC2E]/30 transition-all duration-300">
                <div className="w-12 h-12 rounded-xl bg-[#002301]/5 flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-[#FFB600]" />
                </div>
                <h3 className="text-[#002301] font-bold text-lg mb-2">{title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────── */}
      <section className="bg-[#002301] py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-5">
          <svg width="100%" height="100%"><defs><pattern id="con-grid" width="40" height="40" patternUnits="userSpaceOnUse"><path d="M40 0L0 0 0 40" fill="none" stroke="#17BC2E" strokeWidth="0.8" /></pattern></defs><rect width="100%" height="100%" fill="url(#con-grid)" /></svg>
        </div>
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <motion.p initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-sm font-medium text-[#FFB600] uppercase tracking-widest mb-3">Simple Process</motion.p>
            <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="text-white text-3xl sm:text-4xl font-bold">Getting Started</motion.h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map(({ n, title, desc }, i) => (
              <motion.div key={n} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-colors duration-300">
                <p className="text-[#FFB600] text-2xl font-bold mb-3">{n}</p>
                <h3 className="text-white font-semibold text-base mb-2">{title}</h3>
                <p className="text-white/55 text-sm leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CATEGORIES ───────────────────────────────────── */}
      <section className="bg-white py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <motion.p initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-sm font-medium text-[#17BC2E] uppercase tracking-widest mb-3">Browse the Marketplace</motion.p>
            <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="text-[#002301] text-3xl sm:text-4xl font-bold">What Are You Looking For?</motion.h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {categories.map(({ label, desc, href }, i) => (
              <motion.div key={label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}>
                <Link href={href} className="group block bg-[#f4f6f6] rounded-2xl p-6 border border-transparent hover:border-[#17BC2E]/30 hover:shadow-md transition-all duration-300">
                  <h3 className="text-[#002301] font-bold text-base mb-1 group-hover:text-[#17BC2E] transition-colors">{label}</h3>
                  <p className="text-gray-500 text-sm">{desc}</p>
                  <span className="text-[#FFB600] text-xs font-semibold mt-3 inline-block group-hover:translate-x-1 transition-transform">Shop now →</span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <ReviewSection />

      {/* ── FINAL CTA ────────────────────────────────────── */}
      <section className="bg-[#f4f6f6] py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Zap className="w-10 h-10 text-[#FFB600] mx-auto mb-4" />
            <h2 className="text-[#002301] text-3xl sm:text-4xl font-bold mb-4">
              Ready to <span className="text-[#FFB600]">Switch to Solar?</span>
            </h2>
            <p className="text-gray-500 text-base mb-8 max-w-xl mx-auto">
              Browse our marketplace and connect with trusted solar providers across Nigeria today.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link href="/marketplace" className="inline-flex items-center justify-center gap-2 bg-[#002301] text-white px-8 py-4 rounded-full font-semibold hover:bg-black hover:scale-105 transition-all duration-300">
                Browse Marketplace <ArrowRight className="w-4 h-4" />
              </Link>
              <Link href="/auth/register" className="inline-flex items-center justify-center gap-2 bg-white border border-gray-200 text-[#002301] px-8 py-4 rounded-full font-semibold hover:bg-gray-50 transition-all duration-300">
                Create Free Account
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── TRUST BADGES ─────────────────────────────────── */}
      <TrustBadgesSection />
    </div>
  );
}
